import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { PublicShell } from "@/auth/PublicShell";
import { DOWNLOAD_ERRORS, humanise, redeemDownload, type DownloadTarget } from "@/auth/public_api";

/** An emailed first-install link for a client tool.
 *
 *  THE TOKEN IS THE CREDENTIAL. There is no session here and there cannot be —
 *  the person receiving this has typically never signed in, which is the whole
 *  point: it is how a bench technician gets the installer the first time.
 *  24 hours, capped multi-use (Fabric `ToolDownloadToken`, migration 0021).
 *
 *  ELIGIBILITY IS RE-RESOLVED AT CLICK TIME, not at send time. Access can be
 *  revoked and the release lineup can change between the email landing and the
 *  click, so the server re-checks both and the link goes stale the moment
 *  either does. That is also why this page cannot pre-render what it is about
 *  to hand over — it does not know until the server answers.
 *
 *  This page only RESOLVES the token; the bytes come from the server as an
 *  attachment. Showing what is about to download (name, version, size) before
 *  the transfer starts matters more here than elsewhere: it is a hundreds-of-MB
 *  executable arriving on a factory machine, and "some .exe appeared" is not
 *  something anyone should have to trust blindly.
 */
export function ToolDownloadPage() {
  const { token = "" } = useParams();
  const [state, setState] = useState<"working" | "ready" | "failed">("working");
  const [target, setTarget] = useState<DownloadTarget | null>(null);
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;      // capped multi-use: a double-mount spends a use
    ran.current = true;
    redeemDownload(token)
      .then((t) => { setTarget(t); setState("ready"); })
      .catch((err) => {
        const e = err as { code: string; message?: string };
        setError(humanise(e.code, e.message, DOWNLOAD_ERRORS));
        setState("failed");
      });
  }, [token]);

  if (state === "working") {
    return (
      <PublicShell eyebrow="Orbit Platform" title="Preparing your download" sub="Checking the link.">
        <div className="cap" style={{ textAlign: "center" }}>One moment…</div>
      </PublicShell>
    );
  }

  if (state === "failed") {
    return (
      <PublicShell eyebrow="Orbit Platform" title="This link can't be used" sub={error}>
        <div className="cap" style={{ textAlign: "center" }}>
          Download links are personal - they are issued to one person for one tool.
          Forwarding one does not usually work.
        </div>
      </PublicShell>
    );
  }

  const t = target!;
  return (
    <PublicShell
      eyebrow="Orbit Platform"
      title={`Download ${t.product}`}
      sub={<>Version <strong>{t.version}</strong> - the current release for your account.</>}
    >
      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <div className="mono" style={{ fontSize: 12.5, wordBreak: "break-all" }}>{t.filename}</div>
        <div className="cap" style={{ marginTop: 4 }}>
          {(t.size_bytes / 1_048_576).toFixed(0)} MB
        </div>
      </div>
      {/* The BYTES come from an API prefix, never from this path.
          `/tool-download/:token` is a CLIENT route: the SPA fallback answers
          unmatched paths with index.html and HTTP 200, so a download link
          pointed here would hand over a 400-byte web page wearing an .exe
          filename, with no error anywhere.

          A first version of this comment claimed `/auth/*` was safe because the
          server "owns" that prefix. Curling the box disproved it - the fallback
          matched on an unmatched PATH, not on a prefix, so this very URL
          returned 200 text/html while the endpoint was unbuilt. It is safe NOW
          because `services/app/main.py` was changed to make it so: an unmatched
          path under an API prefix 404s like an API
          (`tests/api/test_spa_fallback_is_not_an_api.py`). */}
      <a className="btn-signin" href={`/auth/tool-download/${encodeURIComponent(token)}`} style={{ textAlign: "center", display: "block" }}>
        Download installer →
      </a>
      <div className="cap" style={{ textAlign: "center", marginTop: 14 }}>
        This link works for 24 hours and a limited number of downloads. Keep the
        installer rather than the link.
      </div>
    </PublicShell>
  );
}
